import { useQuery } from '@tanstack/react-query'
import { useContext, useMemo } from 'react'
import axios from 'axios'
import { UserContext } from 'shared/users/contexts/UserContext'
import { UserRole } from 'hooks/useAuth'
import { minutesToMillis } from 'helpers'
import { CustomerProfileOptionResponse } from 'api/models'
import { customerProfileQueryKeys } from '../keys'

const baseUrl = `${process.env.REACT_APP_API_URL}/v1/customer-profile`

interface DataResponse<T> {
  data: T
}

async function fetchCurrentCustomerProfiles(signal?: AbortSignal) {
  return (await axios.get<DataResponse<CustomerProfileOptionResponse[]>>(`${baseUrl}/option`, { signal })).data.data
}

async function fetchCustomerProfilesByCustomerId(customerId: number, signal?: AbortSignal) {
  return (await axios.get<DataResponse<CustomerProfileOptionResponse[]>>(`${baseUrl}/option/${customerId}`, { signal })).data.data
}

function useCustomerProfilesQuery(customerId: number | null, isExistingDeclaration: boolean) {
  const { user } = useContext(UserContext)
  const isAdmin = useMemo(() => user?.role === UserRole.ADMIN, [user?.role])

  const useProfilesByCustomerId = isAdmin && isExistingDeclaration

  const currentCustomerProfiles = useQuery({
    queryKey: customerProfileQueryKeys.currentCustomer(),
    queryFn: ({ signal }) => fetchCurrentCustomerProfiles(signal),
    enabled: !useProfilesByCustomerId && !!customerId,
    staleTime: minutesToMillis(15),
  })

  const customerProfilesById = useQuery({
    queryKey: customerProfileQueryKeys.byCustomerId(customerId),
    queryFn: ({ signal }) => fetchCustomerProfilesByCustomerId(customerId as number, signal),
    enabled: useProfilesByCustomerId && !!customerId,
    staleTime: minutesToMillis(15),
  })

  return useProfilesByCustomerId ? customerProfilesById : currentCustomerProfiles
}

export default useCustomerProfilesQuery
